import { Component, inject, ChangeDetectionStrategy } from "@angular/core";
import { A11yModule } from "@angular/cdk/a11y";
import { NotificationService } from "../../../services/notification.service";
import { MatDialogClose, MatDialogContent, MatDialogTitle } from "@angular/material/dialog";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatInput, MatPrefix } from "@angular/material/input";
import { MatFormField, MatLabel, MatSuffix } from "@angular/material/form-field";

import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatIcon } from "@angular/material/icon";
import { MatButton, MatIconButton } from "@angular/material/button";
import { JanusService } from "../../../services/janus.service";
import { finalize, first } from "rxjs";
import { TranslatePipe } from "@tolgee/ngx";
import { Protocol } from "../../../interfaces/protocols/protocol";
import { TagComponent } from "../../tags/tag/tag.component";
import { Tag } from "../../../interfaces/tag";
import { ProtocolViewerService } from "../../../services/protocol-viewer.service";
import { ProtocolService } from "../../../services/protocol.service";
import { SafeHtmlPipe } from "../../../pipes/safe-html.pipe";
import { marked } from "marked";

@Component({
	selector: "app-janus",
	templateUrl: "./janus.component.html",
	styleUrls: ["./janus.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		MatButton,
		MatIconButton,
		MatIcon,
		MatDialogClose,
		ReactiveFormsModule,
		FormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatSuffix,
		MatProgressSpinner,
		TranslatePipe,
		TagComponent,
		MatPrefix,
		MatDialogTitle,
		MatDialogContent,
		SafeHtmlPipe,
		A11yModule,
	],
})
export class JanusComponent {
	private toastr = inject(NotificationService);
	private janus = inject(JanusService);
	private protocolViewer = inject(ProtocolViewerService);
	private protocolService = inject(ProtocolService);
	isLoading = false;
	isStreaming = false;
	readonly streamingMode = true;
	searchKeyword = "";
	offset = 0;
	aiAnswer = "";
	rawStreamText = "";
	protocols: Protocol[] = [];

	search(): void {
		if (this.searchKeyword.trim() === "") {
			this.toastr.warning("Merci de renseigner des mots-clés pour la recherche.");
			return;
		}

		this.isLoading = true;
		this.aiAnswer = "";
		this.protocols = [];

		if (this.streamingMode) {
			this.isStreaming = true;
			this.rawStreamText = "";
			this.janus
				.streamQuery(
					this.searchKeyword.trim(),
					(token) => {
						this.rawStreamText += token;
					},
					(sources) => {
						const uniqueSources = sources.filter(
							(s, i, arr) => arr.findIndex((x) => x.protocol_id === s.protocol_id) === i
						);
						this.protocols = uniqueSources.map(
							(s) =>
								({
									id: s.protocol_id,
									label: s.title,
									tags: [],
									updates: [],
								}) as unknown as Protocol
						);
						uniqueSources.forEach((s, index) => {
							this.protocolService
								.getProtocolById(s.protocol_id)
								.pipe(first())
								.subscribe((full) => (this.protocols[index] = full));
						});
					},
					() => {
						this.aiAnswer = marked.parse(this.rawStreamText, { async: false });
						this.isStreaming = false;
						this.isLoading = false;
					},
					(message) => {
						this.toastr.error(message);
						this.isStreaming = false;
						this.isLoading = false;
					}
				)
				.catch((err) => {
					this.toastr.error(err?.message ?? "Stream error");
					this.isStreaming = false;
					this.isLoading = false;
				});
			return;
		}

		this.janus
			.askQuestion(this.searchKeyword.trim())
			.pipe(
				first(),
				finalize(() => (this.isLoading = false))
			)
			.subscribe((response) => {
				this.aiAnswer = marked.parse(response.answer, { async: false });
				const raw = response.protocols ?? [];
				this.protocols = raw.filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i);
				this.isLoading = false;
			});
	}

	copyAnswer(): void {
		navigator.clipboard.writeText(this.isStreaming ? this.rawStreamText : this.aiAnswer);
	}

	getSectionTags(protocol: Protocol): Tag[] {
		return protocol.tags?.filter((t) => t.section) ?? [];
	}

	viewProtocol(protocol: Protocol): void {
		this.protocolViewer.viewProtocol(protocol);
	}
}
